import React, { useState } from 'react';
import { assetUrl } from '../utils/assets';
import { motion } from 'motion/react';
import { SavedSession, formatRelativeTime, getOldestSave } from '../utils/sessionStorage';
import { Variant } from '../utils/variant';
import { fireAnalyticsEvent } from '../utils/analytics';
import InstallIntentModal from './InstallIntentModal';
import './LandingPage.css';

interface LandingPageProps {
  savedSessions: SavedSession[];
  storageAvailable: boolean;
  variant: Variant;
  onNewGame: () => void;
  onResumeGame: (sessionId: string) => void;
  onReplaceOldest: () => void;
  onPrivacyPolicyClick: () => void;
  onTermsClick: () => void;
}

const BOOK_COVER_URL = assetUrl('/assets/chapters/book_cover.png');
const CARDERO_LOGO_URL = assetUrl('/assets/chapters/book_cover_logo.png');
const MAX_SAVES = 3;

const LandingPage: React.FC<LandingPageProps> = ({
  savedSessions,
  storageAvailable,
  variant,
  onNewGame,
  onResumeGame,
  onReplaceOldest,
  onPrivacyPolicyClick,
  onTermsClick,
}) => {
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const hasSaves = storageAvailable && savedSessions.length > 0;
  const isFull = savedSessions.length >= MAX_SAVES;

  const handleNewGameClick = () => {
    if (isFull) {
      setShowReplaceDialog(true);
    } else {
      onNewGame();
    }
  };

  const handleConfirmReplace = () => {
    setShowReplaceDialog(false);
    onReplaceOldest();
  };

  const oldest = isFull ? getOldestSave() : null;

  return (
    <div className="story-pager story-page-hero splash-screen landing-page">
      <div className="splash-particles" aria-hidden="true">
        {Array.from({ length: 10 }, (_, i) => (
          <motion.div
            key={i}
            className="splash-particle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 + i * 0.15 }}
          />
        ))}
      </div>

      <div className="chapter-hero-content">
        <motion.div
          className="chapter-hero-image"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={BOOK_COVER_URL} alt="Percy the Cat book cover" />
        </motion.div>
      </div>

      <motion.div
        className="landing-controls"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6, type: 'spring', stiffness: 200, damping: 20 }}
      >
        {hasSaves && (
          <div className="save-cards">
            {savedSessions.map((session) => (
              <div key={session.sessionId} className="save-card">
                <div className="save-card-info">
                  {savedSessions.length > 1 && (
                    <div className="save-card-label">Adventure {session.adventureNumber}</div>
                  )}
                  <div className="save-card-chapter">
                    {session.chapterTitle || `Chapter ${session.chapterNumber}`}
                    <span className="save-card-time"> · {formatRelativeTime(session.lastPlayedAt)}</span>
                  </div>
                </div>
                <button
                  className="save-card-button"
                  onClick={() => onResumeGame(session.sessionId)}
                >
                  {savedSessions.length === 1 ? 'Continue' : 'Play'}
                </button>
              </div>
            ))}
          </div>
        )}

        {variant === 'install' ? (
          <div className="landing-cta-pair">
            <button className="chapter-start-button" onClick={handleNewGameClick}>
              Play Now
            </button>
            <button
              className="chapter-start-button chapter-install-button"
              onClick={() => {
                fireAnalyticsEvent('install_cta_clicked');
                setShowInstallModal(true);
              }}
            >
              Get the App
            </button>
          </div>
        ) : (
          <button className="chapter-start-button" onClick={handleNewGameClick}>
            New Game
          </button>
        )}

      </motion.div>

      <div className="landing-bottom">
        <a
          href="https://cardero.games/"
          target="_blank"
          rel="noopener noreferrer"
          className="landing-cardero-logo"
          aria-label="Cardero Games"
        >
          <img src={CARDERO_LOGO_URL} alt="Cardero Games" />
        </a>
        <div className="landing-footer">
          <a href="https://discord.gg/RM7zFmFzHQ" target="_blank" rel="noopener noreferrer">
            <img src={assetUrl('/assets/icons/discord.svg')} alt="Discord" className="landing-footer-icon" />
            Discord
          </a>
          <span className="landing-footer-sep">·</span>
          <a href="#" onClick={(e) => { e.preventDefault(); onPrivacyPolicyClick(); }}>Privacy Policy</a>
          <span className="landing-footer-sep">·</span>
          <a href="#" onClick={(e) => { e.preventDefault(); onTermsClick(); }}>Terms</a>
        </div>
      </div>

      {showInstallModal && (
        <InstallIntentModal
          variant={variant}
          onClose={() => setShowInstallModal(false)}
          onPlayNow={() => {
            fireAnalyticsEvent('install_modal_play_clicked');
            setShowInstallModal(false);
            handleNewGameClick();
          }}
        />
      )}

      {showReplaceDialog && oldest && (
        <div className="replace-dialog-overlay" onClick={() => setShowReplaceDialog(false)}>
          <div className="replace-dialog" onClick={(e) => e.stopPropagation()}>
            <p>
              Starting a new game will replace your oldest adventure
              (Adventure {oldest.adventureNumber} · {oldest.chapterTitle || `Chapter ${oldest.chapterNumber}`}).
            </p>
            <div className="replace-dialog-buttons">
              <button className="replace-dialog-cancel" onClick={() => setShowReplaceDialog(false)}>
                Cancel
              </button>
              <button className="replace-dialog-confirm" onClick={handleConfirmReplace}>
                Start New Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
